import React from 'react';
import TasklistJsonPageHeader from '@/components/TasklistJsonPageHeader/TasklistJsonPageHeader';
import Button from '@/components/Button/Button';
import { RedirectIcon } from '@/components/MenusIcons/MenusIcons';
import { useOverviewContext } from '@/context/OverviewContext';
import { useShipping } from '@/context/ShippingContext';
import { ShippingDetail } from '@/services/shippingApiService';
import config from '@/config';
import { getMappedStatus, getVisibleShippingItems, isLaptopRequestItem } from '@/utils/shippingUtils';
import { formatShipDate } from '@/utils/dateUtils';
import laptopImg from '@/assets/icons/material-laptop.png';
import boxImg from '@/assets/icons/material-box.png';

const WHEN_TO_EXPECT_BODY =
  "The materials a student needs depend on their courses, so you'll hear from a placement coordinator soon after enrollment is complete. Depending on grade and course level, your school may need to complete a transcript analysis to place your student in appropriate courses. Once courses are assigned, materials ship automatically and will be delivered within four to seven business days after shipment.";

const SUPPLIES_BODY =
  'K12 provides curriculum and special supplies students need to complete their coursework, often including a school computer. K12 does not supply printer ink and paper or basic items like notebooks, folders, and pens. Your teachers may have further suggestions for specific items you may want to have on hand to support learning.';

const SUPPLIES_BODY_PRIVATE =
  'K12 provides curriculum and special supplies students need to complete their coursework. K12 does not supply printer ink and paper or basic items like notebooks, folders, and pens. Your teachers may have further suggestions for specific items you may want to have on hand to support learning.';

const DISCLAIMER_PUBLIC =
  '*Families do not pay tuition for a student to attend an online public school. Materials provided vary by course, state, school, and grade level. Common household items and office supplies, like printer ink and paper, are not provided. Our enrollment consultants can help address your technology and computer questions and needs.';

const DISCLAIMER_PRIVATE =
  '*Computers, common household items and office supplies, like printer ink and paper, are not provided. Our enrollment consultants can help address your technology and computer questions and needs.';

const FAQS = [
  {
    question: "What if our classroom materials don't arrive before school starts?",
    answer:
      'Your school is aware of the timing of course placement and will accommodate a delay in accessing supplies. Students can access online course content from a personal or public computer, such as at a public library, while waiting for classroom materials.',
  },
  {
    question: 'How many boxes of materials will we receive?',
    answer:
      "The number of boxes depends on your student's grade level and the courses they are enrolled in. Most families receive between one and three boxes spread out over the first few weeks of school.",
  },
  {
    question: "What if there's a problem with our shipping address?",
    answer:
      'Contact your enrollment consultant or school office as soon as possible. They can update your shipping address and reroute any pending shipments.',
  },
  {
    question: 'What do we do if we need to return materials?',
    answer:
      'At the end of the school year, your school will send instructions for returning reusable items like the school computer and textbooks. Prepaid shipping labels are typically included.',
  },
] as const;

interface ContentSectionProps {
  title: string;
  body: string;
}

const ContentSection: React.FC<ContentSectionProps> = ({ title, body }) => (
  <section className="classroom-materials-page__section">
    <h2>{title}</h2>
    <p>{body}</p>
  </section>
);

interface StatusCardProps {
  eyebrow: string;
  children: React.ReactNode;
}

const StatusCard: React.FC<StatusCardProps> = ({ eyebrow, children }) => (
  <div className="classroom-materials-page__status-card">
    <div className="classroom-materials-page__status-card-header">
      <span className="eyebrow">{eyebrow}</span>
    </div>
    <div className="classroom-materials-page__status-card-body">{children}</div>
  </div>
);

function getTrackingLink(carrier: string | null, trackingNumber: string | null) {
  const method = carrier?.toLowerCase() || '';
  if (method.includes('ups') && trackingNumber) {
    return { label: 'UPS', url: `${config.TRACKING_LINKS.UPS}${trackingNumber}` };
  }
  if (method.includes('usps') && trackingNumber) {
    return { label: 'USPS', url: `${config.TRACKING_LINKS.USPS}${trackingNumber}` };
  }
  if (method.includes('fedex') && trackingNumber) {
    return { label: 'FedEx', url: `${config.TRACKING_LINKS.FedEx}${trackingNumber}` };
  }
  return null;
}

function getStatusHeadline(item: ShippingDetail): string {
  const mapped = getMappedStatus(item.status);
  if (mapped?.toLowerCase() === 'shipped' && item.shipDate) {
    return `Shipped on ${formatShipDate(item.shipDate)}`.trim();
  }
  return mapped || item.status;
}

function getItemDetailText(item: ShippingDetail): string {
  if (isLaptopRequestItem(item)) {
    if (item.itemDescription?.trim()) {
      return item.itemDescription;
    }
    return 'We have received your laptop request, but eligibility has not yet been determined.';
  }
  return item.itemDescription
    ? `Course Materials: ${item.itemDescription}`
    : 'Course Materials';
}

interface TrackingInformationProps {
  loading: boolean;
  error: string | null;
  items: ShippingDetail[];
}

const TrackingInformation: React.FC<TrackingInformationProps> = ({ loading, error, items }) => {
  return (
    <section className="classroom-materials-page__section">
      <h2>Tracking Information</h2>
      {loading && <p className="classroom-materials-page__status-message">Loading shipments…</p>}
      {error && <p className="classroom-materials-page__status-message">Error: {error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="classroom-materials-page__status-message">
          If available, statuses for each package of classroom materials will appear here once
          processed. For more information, see the &ldquo;When to Expect K12-Provided
          Materials&rdquo; section below.
        </p>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="classroom-materials-page__status-cards">
          {items.map((item, index) => {
            const tracking = getTrackingLink(item.carrier, item.trackingNumber);
            const isLaptop = isLaptopRequestItem(item);
            return (
              <StatusCard key={`shipment-${index}`} eyebrow={`Item ${index + 1} of ${items.length}`}>
                <div className="classroom-materials-page__status-headline-row">
                  <p className="classroom-materials-page__status-headline">{getStatusHeadline(item)}</p>
                  {tracking && (
                    <Button
                      label={`Track Package (${tracking.label})`}
                      size="lg"
                      variant="border"
                      color="primary"
                      width="fit-content"
                      icon={<RedirectIcon />}
                      iconPosition="after"
                      linkUrl={tracking.url}
                      linkTarget="_blank"
                      className="classroom-materials-page__track-btn"
                    />
                  )}
                </div>
                <div
                  className={`classroom-materials-page__status-detail${
                    isLaptop ? '' : ' classroom-materials-page__status-detail--centered'
                  }`}
                >
                  <img
                    src={isLaptop ? laptopImg : boxImg}
                    alt={isLaptop ? 'Laptop' : 'Box'}
                    className="classroom-materials-page__status-icon"
                  />
                  <p>{getItemDetailText(item)}</p>
                </div>
              </StatusCard>
            );
          })}
        </div>
      )}
    </section>
  );
};

const ClassroomMaterialsPage = () => {
  const { schoolTypeConfigured } = useOverviewContext();
  const { shippingData, loading, error } = useShipping();
  const visibleItems = getVisibleShippingItems(shippingData);
  const isPrivate = schoolTypeConfigured === 'private';

  const heroDescription = isPrivate
    ? "Get ready to see your student unbox a great education! Your student's classroom materials — textbooks, supplies for offline activities, and more — are provided at no cost to your family.*"
    : "Get ready to see your student unbox a great education! Your student's classroom materials — a school computer, textbooks, supplies for offline activities, and more — are provided at no cost to your family.*";

  return (
    <div className="classroom-materials-page tasklist-json-page tasklist-json-page--tasklist">
      <TasklistJsonPageHeader
        variant="tasklist"
        learningCenterHubLanding
        title="Classroom Materials Tracker"
        description={heroDescription}
      />

      <div className="tasklist-json-body">
        <div className="tasklist-json-post-layout classroom-materials-page__content">
          <TrackingInformation loading={loading} error={error} items={visibleItems} />

          <ContentSection title="When to Expect K12-Provided Materials" body={WHEN_TO_EXPECT_BODY} />

          <ContentSection
            title="School Supplies You'll Need to Provide"
            body={isPrivate ? SUPPLIES_BODY_PRIVATE : SUPPLIES_BODY}
          />

          <section className="classroom-materials-page__section classroom-materials-page__faqs">
            <h2>FAQs</h2>
            <div className="classroom-materials-page__faq-list">
              {FAQS.map((item) => (
                <div key={item.question} className="classroom-materials-page__faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <p className="classroom-materials-page__disclaimer">
            {isPrivate ? DISCLAIMER_PRIVATE : DISCLAIMER_PUBLIC}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClassroomMaterialsPage;
